import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "@material/web/button/text-button.js";
import "@material/web/icon/icon.js";

import { AuthController } from "../lib/auth-controller";
import { logout } from "../lib/pb";
import { navigate } from "../lib/navigation";
import { icon } from "../lib/icons";
import "./material/dpp-top-app-bar";

interface NavItem {
  href: string;
  label: string;
  icon: string;
}

interface NavGroup {
  /** Tier heading; omitted for the top acquisition group. */
  header?: string;
  items: NavItem[];
}

/**
 * Internal B2B operator console. Navigation leads with the product-line
 * oversight tiers, in the same capability hierarchy the tenant console uses —
 * ANTI-COUNTERFEIT (the flagship: the counterfeit register) then GEOGRAPHICAL
 * INDICATION (the authority tree) — followed by ONBOARDING, the acquisition
 * funnel (Producers → Pipeline). DPP has no operator surface — it is a
 * tenant-managed regulatory layer — so it is intentionally absent here.
 */
const NAV_GROUPS: NavGroup[] = [
  {
    header: "Anti-counterfeit",
    items: [{ href: "/counterfeit", label: "Counterfeits", icon: "flag" }],
  },
  {
    header: "Geographical Indication",
    items: [{ href: "/authorities", label: "Authorities", icon: "account_tree" }],
  },
  {
    header: "Onboarding",
    items: [
      { href: "/companies", label: "Producers", icon: "factory" },
      { href: "/provisioning", label: "Pipeline", icon: "hub" },
    ],
  },
];

/** Console chrome. Mobile-first (design system §6): Compact (<840px) gets a
 * sticky top app bar whose menu button opens a modal navigation drawer (M3
 * drawer spec — 56px destinations, labels, scrim); Medium+ keeps the
 * persistent 248px rail with the content on a rounded surface sheet. */
@customElement("app-shell")
export class AppShell extends LitElement {
  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      min-height: 100dvh;
    }

    .shell {
      min-height: 100vh;
      min-height: 100dvh;
    }

    md-icon svg {
      width: 1em;
      height: 1em;
      fill: currentColor;
    }

    .mobile-bar {
      display: block;
      position: sticky;
      top: 0;
      z-index: 10;
      border-bottom: 1px solid var(--md-sys-color-outline-variant);
      padding-top: env(safe-area-inset-top, 0px);
      background: var(--md-sys-color-surface-container);
    }

    .bar-brand {
      display: inline-flex;
      color: var(--md-sys-color-primary);
      --md-icon-size: 24px;
    }

    .menu-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      border: none;
      border-radius: 999px;
      background: transparent;
      color: var(--md-sys-color-on-surface);
      cursor: pointer;
      --md-icon-size: 24px;
    }

    .menu-btn:active {
      background: color-mix(
        in srgb,
        var(--md-sys-color-on-surface) 10%,
        transparent
      );
    }

    .nav {
      position: fixed;
      inset-block: 0;
      inset-inline-start: 0;
      z-index: 30;
      width: min(304px, 85vw);
      display: flex;
      flex-direction: column;
      padding: var(--space-4, 16px) var(--space-3, 12px);
      padding-inline-start: calc(
        var(--space-3, 12px) + env(safe-area-inset-left, 0px)
      );
      box-sizing: border-box;
      gap: 2px;
      overflow-y: auto;
      background: var(--md-sys-color-surface-container-high);
      border-start-end-radius: 16px;
      border-end-end-radius: 16px;
      transform: translateX(-105%);
      visibility: hidden;
      transition:
        transform 0.2s ease,
        visibility 0.2s;
    }

    :dir(rtl) .nav {
      transform: translateX(105%);
    }

    .nav--open {
      transform: none;
      visibility: visible;
    }

    .scrim {
      position: fixed;
      inset: 0;
      z-index: 20;
      background: var(--md-sys-color-scrim, #000);
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s ease;
      border: none;
      padding: 0;
    }

    .scrim--open {
      opacity: 0.4;
      pointer-events: auto;
    }

    .brand {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 12px 20px;
      font: 400 18px/1.2 var(--font-brand, Roboto, sans-serif);
      color: var(--md-sys-color-on-surface);
    }

    .brand small {
      color: var(--md-sys-color-on-surface-variant);
      font-size: 12px;
    }

    .brand__icon {
      display: inline-flex;
      color: var(--md-sys-color-primary);
      --md-icon-size: 24px;
    }

    nav {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .nav__group {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .nav__group + .nav__group {
      margin-top: 14px;
    }

    /* Tier label — the anti-counterfeit → GI oversight hierarchy made visible,
       mirroring the tenant console. */
    .nav__group-header {
      padding: 6px 16px 4px;
      font: 600 11px/1.3 var(--font-plain, sans-serif);
      letter-spacing: 0.09em;
      text-transform: uppercase;
      color: var(--md-sys-color-on-surface-variant);
      opacity: 0.7;
    }

    .nav__item {
      display: flex;
      align-items: center;
      gap: 12px;
      height: 56px;
      padding: 0 16px;
      border-radius: 999px;
      text-decoration: none;
      color: var(--md-sys-color-on-surface-variant);
      font: 500 14px/1 var(--font-plain, sans-serif);
      transition: background-color 0.15s ease;
    }

    .nav__item:hover:not(.nav__item--active),
    .nav__item:focus-visible:not(.nav__item--active) {
      background: color-mix(
        in srgb,
        var(--md-sys-color-on-surface) 8%,
        transparent
      );
    }

    .nav__item:focus-visible,
    .menu-btn:focus-visible,
    .scrim:focus-visible {
      outline: 2px solid var(--md-sys-color-primary);
      outline-offset: 2px;
    }

    .nav__item--active {
      background: var(--md-sys-color-secondary-container);
      color: var(--md-sys-color-on-secondary-container);
    }

    .nav__icon {
      display: inline-flex;
      --md-icon-size: 22px;
    }

    .nav__footer {
      margin-top: auto;
      padding-top: 16px;
      padding-bottom: env(safe-area-inset-bottom, 0px);
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .user {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 0 4px 0 16px;
    }

    .user__name {
      font-size: 0.8125rem;
      color: var(--md-sys-color-on-surface-variant);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }

    .content {
      min-width: 0;
      background: var(--md-sys-color-surface);
    }

    @media (min-width: 840px) {
      :host {
        height: 100vh;
        height: 100dvh;
      }

      .shell {
        display: flex;
        height: 100%;
      }

      .mobile-bar,
      .scrim {
        display: none;
      }

      .nav {
        position: static;
        z-index: auto;
        width: 248px;
        flex: none;
        transform: none;
        visibility: visible;
        background: transparent;
        border-radius: 0;
        transition: none;
      }

      .content {
        flex: 1;
        overflow-y: auto;
        border-start-start-radius: 16px;
        border-end-start-radius: 16px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .nav,
      .scrim,
      .nav__item {
        transition: none;
      }
    }
  `;

  @property({ type: String }) active = "";

  @state() private drawerOpen = false;

  private wideQuery = window.matchMedia("(min-width: 840px)");

  @state() private isWide = this.wideQuery.matches;

  private auth = new AuthController(this);

  private onWideChange = (e: MediaQueryListEvent): void => {
    this.isWide = e.matches;
    if (e.matches) this.drawerOpen = false;
  };

  private onKeydown = (e: KeyboardEvent): void => {
    if (e.key === "Escape" && this.drawerOpen) this.closeDrawer();
  };

  connectedCallback(): void {
    super.connectedCallback();
    this.wideQuery.addEventListener("change", this.onWideChange);
    window.addEventListener("keydown", this.onKeydown);
  }

  disconnectedCallback(): void {
    this.wideQuery.removeEventListener("change", this.onWideChange);
    window.removeEventListener("keydown", this.onKeydown);
    super.disconnectedCallback();
  }

  private toggleDrawer = (): void => {
    this.drawerOpen = !this.drawerOpen;
  };

  private closeDrawer = (): void => {
    this.drawerOpen = false;
  };

  private handleLogout = (): void => {
    logout();
    void navigate("/login");
  };

  render() {
    const user = this.auth.user;
    const drawerHidden = !this.isWide && !this.drawerOpen;
    return html`
      <dpp-top-app-bar class="mobile-bar" headline="Prospecting & Onboarding">
        <button
          slot="leading"
          class="menu-btn"
          aria-label="Open navigation menu"
          aria-expanded=${this.drawerOpen ? "true" : "false"}
          @click=${this.toggleDrawer}
        >
          ${icon("menu")}
        </button>
        <span slot="leading" class="bar-brand">${icon("hub")}</span>
        <md-text-button slot="trailing" @click=${this.handleLogout}>
          Sign out
        </md-text-button>
      </dpp-top-app-bar>
      <div class="shell">
        <button
          class="scrim ${this.drawerOpen ? "scrim--open" : ""}"
          aria-label="Close navigation menu"
          tabindex=${this.drawerOpen ? "0" : "-1"}
          @click=${this.closeDrawer}
        ></button>
        <aside
          class="nav ${this.drawerOpen ? "nav--open" : ""}"
          ?inert=${drawerHidden}
          aria-label="Main navigation"
        >
          <div class="brand">
            <span class="brand__icon">${icon("hub")}</span>
            <span>Prospecting<br /><small>&amp; Onboarding</small></span>
          </div>
          <nav>
            ${NAV_GROUPS.map(
              (group) => html`
                <div class="nav__group">
                  ${group.header
                    ? html`<div class="nav__group-header">
                        ${group.header}
                      </div>`
                    : null}
                  ${group.items.map(
                    (item) => html`
                      <a
                        href=${item.href}
                        aria-label=${item.label}
                        aria-current=${this.active === item.href
                          ? "page"
                          : "false"}
                        class="nav__item ${this.active === item.href
                          ? "nav__item--active"
                          : ""}"
                        @click=${this.closeDrawer}
                      >
                        <span class="nav__icon">${icon(item.icon)}</span>
                        <span class="nav__label">${item.label}</span>
                      </a>
                    `,
                  )}
                </div>
              `,
            )}
          </nav>
          <div class="nav__footer">
            <div class="user">
              <span class="user__name"
                >${user?.display_name || user?.email}</span
              >
              <md-text-button @click=${this.handleLogout}>
                Sign out
              </md-text-button>
            </div>
          </div>
        </aside>
        <main class="content">
          <slot></slot>
        </main>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-shell": AppShell;
  }
}
